import classNames from 'classnames'
import { t } from 'i18next'
import React, { Fragment, KeyboardEvent, ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Card, FormError, IconFa, IconLoading, IconUpload, IconUploadDrag, PlainButton, ProgressBar } from '../../atoms'
import { useBreakpoint } from '../../utils'
import styles from './FormUpload.module.css'

export interface FileCardProps {
  imageSrc: string
  onDelete: (file: UploadFile) => void
  file: UploadFile
  previewImageAltText?: string
}

export interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  text?: string
  description?: ReactNode
  maxAmountOfFiles: number
  files: Array<UploadFile>
  accept: string
  isLoading?: boolean
  onDelete: (file: UploadFile) => void
  onEdit: React.ChangeEventHandler<HTMLInputElement> | undefined
  validations: Array<(filelist: FileList | null) => string | undefined>
  FileCardMobile: React.FC<FileCardProps>
  FileCardDesktop: React.FC<FileCardProps>
  errorMessage?: ReactNode
}

enum ViewState {
  desktop = 0,
  mobile = 1,
  fileAmountReached = 2,
  editMobile = 3,
  singleLoadingMobile = 4
}

export interface UploadFile extends File {
  progress?: number
}

export function FormUpload({
  text,
  description,
  files,
  onDelete,
  onEdit,
  validations,
  maxAmountOfFiles,
  FileCardMobile,
  FileCardDesktop,
  errorMessage,
  isLoading,
  ...props
}: Props): ReactElement {
  const isDesktop = useBreakpoint('md')
  const inputRef = useRef<HTMLInputElement>(null)
  const [showDraggingStyle, setShowDraggingStyle] = useState(false)
  const [viewState, setViewState] = useState(isDesktop ? ViewState.desktop : ViewState.mobile)
  const filesWithImageSrc = useMemo(() => files.map((file) => ({ file, imageSrc: URL.createObjectURL(file) })), [files])
  const [internalError, setInternalError] = useState('')
  const { value, ...restProps } = props // destructure props to not include value at all - invalid attribute for input type file

  useEffect(() => {
    const isFileAmountReached = files.length >= maxAmountOfFiles
    const firstFileProgress = files.at(0)?.progress
    if (!isDesktop && files.length === 1 && isFileAmountReached && firstFileProgress && firstFileProgress > 0) {
      setViewState(ViewState.singleLoadingMobile)
    } else if (!isDesktop && files.length === 1 && isFileAmountReached) {
      setViewState(ViewState.editMobile)
    } else if (isFileAmountReached) {
      setViewState(ViewState.fileAmountReached)
    } else {
      setViewState(isDesktop ? ViewState.desktop : ViewState.mobile)
    }
  }, [isDesktop, maxAmountOfFiles, files])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowDraggingStyle(false)
    setInternalError('')

    const validationError = validations
      .map((fn) => fn(event.target.files))
      .filter((error): error is string => !!error)
      .at(0)
    if (validationError) {
      setInternalError(validationError)
    } else if (viewState === ViewState.editMobile) {
      onEdit && onEdit(event)
    } else {
      props.onChange && props.onChange(event)
    }
  }

  const handleDelete = (file: UploadFile) => {
    if (inputRef.current) {
      const dataTransfer = new DataTransfer()
      files
        .filter((x) => x.name + x.lastModified !== file.name + file.lastModified)
        .map((x) => {
          dataTransfer.items.add(x)
        })
      inputRef.current.files = dataTransfer.files
    }
    onDelete(file)
  }

  const onKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key.toLocaleLowerCase() === 'enter') {
      inputRef.current?.click()
    }
  }

  const error = props['aria-invalid'] || Boolean(internalError)

  return (
    <div>
      {!isDesktop && (
        <Fragment>
          <div className={classNames(styles.uploadDescription, styles.smallDescription)}>{description}</div>
        </Fragment>
      )}
      <div className={styles.container}>
        {filesWithImageSrc.map(({ file, imageSrc }) =>
          !isDesktop && maxAmountOfFiles !== 1 ? (
            <FileCardMobile key={file.name + file.size} imageSrc={imageSrc} onDelete={handleDelete} file={file} />
          ) : null
        )}
        <div
          className={classNames(
            styles.button,
            !isDesktop && styles.smallButton,
            props.disabled || viewState === ViewState.fileAmountReached ? styles.disabled : '',
            showDraggingStyle && styles.dragging,
            error && styles.hasError
          )}
          tabIndex={0}
          role="button"
          onKeyUp={onKeyUp}
          onDragOver={() => setShowDraggingStyle(true)}
          onDragLeave={() => setShowDraggingStyle(false)}
          style={{
            backgroundImage:
              viewState === ViewState.editMobile && !isLoading ? `url(${filesWithImageSrc.at(0)?.imageSrc})` : ''
          }}
        >
          <FormUploadIcon
            fileTypes={props.accept}
            viewState={viewState}
            isLoading={isLoading}
            showDraggingStyle={showDraggingStyle}
          />
          <input
            type="file"
            disabled={viewState === ViewState.fileAmountReached || isLoading}
            {...restProps}
            ref={inputRef}
            aria-invalid={error}
            onBlur={(event) => {
              // Stop propagating blur event, since this triggers form validation
              event.stopPropagation()
            }}
            multiple={false}
            className="absolute w-full h-full opacity-0 top-0 left-0"
            onChange={handleChange}
          />
          {viewState === ViewState.editMobile ? (
            <span className={styles.iconContainer}>
              <IconFa icon={['far', 'pen']} />
            </span>
          ) : null}
          {isDesktop && !isLoading ? (
            <Fragment>
              <div className={styles.uploadTitle}>{text}</div>
              <div className={styles.uploadDescription}>{description}</div>
            </Fragment>
          ) : isDesktop ? (
            <div className={styles.uploadTitle}>{t('glow.loading')}...</div>
          ) : null}
        </div>
        {filesWithImageSrc.map(({ file, imageSrc }) =>
          isDesktop ? (
            <FileCardDesktop key={file.name + file.size} file={file} imageSrc={imageSrc} onDelete={handleDelete} />
          ) : null
        )}
      </div>
      <FormError className="mt-2" showError={!!error} errorMessage={internalError || errorMessage} />
    </div>
  )
}

const FormUploadIcon = ({
  fileTypes,
  viewState,
  isLoading,
  showDraggingStyle
}: {
  fileTypes: string
  viewState: ViewState
  isLoading?: boolean
  showDraggingStyle: boolean
}) => {
  const acceptsImages = fileTypes.split(',').some((fileType) => fileType.includes('image'))
  return viewState === ViewState.singleLoadingMobile ? (
    <div className={styles.iconContainer}>
      <IconLoading />
    </div>
  ) : isLoading ? (
    <IconLoading className="w-8 h-8 text-primary-500 mb-2" />
  ) : showDraggingStyle ? (
    <IconUploadDrag />
  ) : viewState !== ViewState.editMobile && acceptsImages ? (
    <IconUpload className="text-gray-400" />
  ) : viewState !== ViewState.editMobile ? (
    <IconFa icon={['far', 'file-plus']} className="w-8 h-8 text-gray-400" />
  ) : null
}

export const FileCardMobile = ({ imageSrc, onDelete, file }: FileCardProps) => (
  <PlainButton
    style={{ backgroundImage: `url(${imageSrc})`, backgroundSize: 'cover' }}
    className={styles.fileCardMobile}
    key={file.size + file.name}
    type="button"
    onClick={() => onDelete(file)}
  >
    <div className={styles.iconContainer}>
      {file.progress && file.progress > 0 && file.progress < 100 ? (
        <ProgressBar percent={file.progress} />
      ) : (
        <IconFa icon={['far', 'trash-alt']} />
      )}
    </div>
  </PlainButton>
)

export const FileCardDesktop = ({ file, imageSrc, onDelete, previewImageAltText = 'Preview image' }: FileCardProps) => (
  <Card key={file.size + file.name} className={styles.fileCardDesktop}>
    <div className={styles.container}>
      <div>
        {file.type.includes('image') ? (
          <img src={imageSrc} alt={previewImageAltText} />
        ) : (
          <IconFa icon={['far', 'file']} className="w-8 h-8 p-4 bg-primary-100 text-primary-500" />
        )}
      </div>
      <div className={styles.content}>
        <div className={styles.title}>{file.name}</div>
        {file.progress && file.progress > 0 && file.progress < 100 ? (
          <ProgressBar percent={file.progress} />
        ) : (
          <div className={styles.description}>{Math.round(file.size / 1000)} KB</div>
        )}
      </div>
      <div className={styles.buttonContainer}>
        <PlainButton onClick={() => onDelete(file)}>
          <IconFa icon={['far', 'trash-alt']} />
        </PlainButton>
      </div>
    </div>
  </Card>
)
